import { getContext, put, takeLatest } from 'redux-saga/effects';
import { ProjectId } from 'api/models';
import { getProjectFromCollection } from 'collections/project';
import { createDependentTask, getProjectTasksFromCollection, updateDependentTasks, updateTaskRecord } from 'collections/tasks';
import { getQueryClientContext } from 'data/core/sagas/query-client';
import { formatDate, getDueDateFromGantt } from 'data/projects/gantt/dates';
import { getGanttTaskId } from 'data/projects/gantt/ids';
import { getPhaseList, getSprintList, getTaskList, normaliseTasks } from 'data/projects/gantt/list-helpers';
import { updateTaskDates } from 'data/projects/sagas/project/date-shift';
import { getQueryAdvancedSettingsConfigKey, getNewDependenciesEnabledSetting } from 'queries/advanced-settings/advanced-settings';
import { getProjectDependencyMapKey, getProjectSprintsQueryKey } from 'queries/common';
import { getTaskDetailFieldsQueryKey } from 'queries/task';
import { DependencyTypesEnumReverse, EntityType, getDateShiftsForEntity } from 'utils/date-shift';
import { createDependencySagaAction, dependencyCreatedAction, dependencyCreationThroughTimeline } from '../actions';
export function populateTaskWithNullDates(projectId, taskId, taskList, taskUpdates, taskStore, payloadTaskUpdates) {
    // Exit early if there's no payload data for date shifts.
    if (!payloadTaskUpdates) {
        return;
    }
    let startDate;
    let dueDate;
    // Use taskStore if available to get the current task record from Gantt.
    if (taskStore) {
        var _ref, _ref1;
        var _payloadTaskUpdates_taskId, _payloadTaskUpdates_taskId1;
        const ganttId = getGanttTaskId(taskId);
        const record = taskStore.getById(ganttId);
        // Reset calculated flags for start and due dates.
        record.set('isCalculatedStartDate', false);
        record.set('isCalculatedEndDate', false);
        // Determine start and due dates using payload data or fallback to record data.
        startDate = (_ref = (_payloadTaskUpdates_taskId = payloadTaskUpdates[taskId]) === null || _payloadTaskUpdates_taskId === void 0 ? void 0 : _payloadTaskUpdates_taskId.startDate) !== null && _ref !== void 0 ? _ref : formatDate(record.getStartDate());
        dueDate = (_ref1 = (_payloadTaskUpdates_taskId1 = payloadTaskUpdates[taskId]) === null || _payloadTaskUpdates_taskId1 === void 0 ? void 0 : _payloadTaskUpdates_taskId1.dueDate) !== null && _ref1 !== void 0 ? _ref1 : getDueDateFromGantt(record);
    } else if (taskList[taskId]) {
        var _ref2, _ref3;
        var _payloadTaskUpdates_taskId2, _taskList_taskId, _payloadTaskUpdates_taskId3, _taskList_taskId1;
        startDate = (_ref2 = (_payloadTaskUpdates_taskId2 = payloadTaskUpdates[taskId]) === null || _payloadTaskUpdates_taskId2 === void 0 ? void 0 : _payloadTaskUpdates_taskId2.startDate) !== null && _ref2 !== void 0 ? _ref2 : (_taskList_taskId = taskList[taskId]) === null || _taskList_taskId === void 0 ? void 0 : _taskList_taskId.startDate;
        dueDate = (_ref3 = (_payloadTaskUpdates_taskId3 = payloadTaskUpdates[taskId]) === null || _payloadTaskUpdates_taskId3 === void 0 ? void 0 : _payloadTaskUpdates_taskId3.dueDate) !== null && _ref3 !== void 0 ? _ref3 : (_taskList_taskId1 = taskList[taskId]) === null || _taskList_taskId1 === void 0 ? void 0 : _taskList_taskId1.dueDate;
    }
    // If we have valid dates, update the task and push the update.
    if (startDate && dueDate) {
        updateTaskRecord(projectId, taskId, {
            startDate,
            dueDate
        });
        taskUpdates.push({
            id: taskId,
            startDate,
            dueDate
        });
    }
}
function* handleEmptyTaskDates(action) {
    var _ganttRef_instance;
    const { payload } = action;
    if (!payload) {
        return;
    }
    const { taskId, projectId, dependentTaskId, canAssignDates, alignPhaseDates, isDatesPresent, newDependenciesSettingEnabled: payloadNewDepsEnabled } = payload;
    let { taskUpdates: payloadTaskUpdates } = payload;
    if (!canAssignDates || !taskId || isDatesPresent) {
        return;
    }
    const queryClient = yield getQueryClientContext();
    // Detail is collection-backed.
    const project = getProjectFromCollection(projectId);
    if (!project) {
        return;
    }
    const sprints = queryClient.getQueryData(getProjectSprintsQueryKey(ProjectId(projectId)));
    const queryTasks = getProjectTasksFromCollection(projectId);
    const tasks = normaliseTasks(queryTasks);
    const accountSettings = queryClient.getQueryData(getQueryAdvancedSettingsConfigKey());
    const computedNewDepsEnabled = getNewDependenciesEnabledSetting(accountSettings);
    const { projectPhases } = project;
    const phaseList = getPhaseList(projectPhases);
    const taskList = getTaskList(tasks, phaseList);
    const sprintList = getSprintList(sprints !== null && sprints !== void 0 ? sprints : [], phaseList);
    const ganttRef = yield getContext('ganttRef');
    const taskStore = ganttRef === null || ganttRef === void 0 ? void 0 : (_ganttRef_instance = ganttRef.instance) === null || _ganttRef_instance === void 0 ? void 0 : _ganttRef_instance.taskStore;
    const _task = taskList[taskId];
    const taskUpdates = [];
    // Helper to update a task's dates if they are missing.
    const updateIfMissing = (id)=>{
        const task = taskList[id];
        if (!task.hasStartDate || !task.hasDueDate) {
            populateTaskWithNullDates(projectId, id, taskList, taskUpdates, taskStore, payloadTaskUpdates);
        }
    };
    // Initialize payloadTaskUpdates if not already provided.
    if (!payloadTaskUpdates) {
        let entityPayload;
        if (!dependentTaskId && payloadNewDepsEnabled) {
            var _taskList_taskId;
            entityPayload = {
                id: taskId,
                dependentOn: [
                    ...((_taskList_taskId = taskList[taskId]) === null || _taskList_taskId === void 0 ? void 0 : _taskList_taskId.dependentOn) || []
                ],
                type: EntityType.Task,
                depth: taskList[taskId].depth
            };
        } else if (dependentTaskId) {
            var _taskList_taskId1, _taskList_dependentTaskId;
            entityPayload = computedNewDepsEnabled ? {
                id: taskId,
                dependentOn: [
                    ...((_taskList_taskId1 = taskList[taskId]) === null || _taskList_taskId1 === void 0 ? void 0 : _taskList_taskId1.dependentOn) || []
                ],
                type: EntityType.Task,
                depth: taskList[taskId].depth
            } : {
                id: dependentTaskId,
                dependencyFor: [
                    ...((_taskList_dependentTaskId = taskList[dependentTaskId]) === null || _taskList_dependentTaskId === void 0 ? void 0 : _taskList_dependentTaskId.dependencyFor) || [],
                    {
                        id: taskId,
                        dependencyType: 2,
                        lag: null
                    }
                ],
                type: EntityType.Task,
                depth: taskList[dependentTaskId].depth
            };
        }
        const [_, _taskUpdates] = getDateShiftsForEntity(phaseList, taskList, sprintList, entityPayload, {
            alignPhaseWithTask: alignPhaseDates !== null && alignPhaseDates !== void 0 ? alignPhaseDates : true,
            dependencyOperationsDone: true,
            enabledNewDependencies: computedNewDepsEnabled
        });
        payloadTaskUpdates = _taskUpdates;
    }
    // Process tasks based on whether a dependent task exists.
    if (!dependentTaskId && payloadNewDepsEnabled) {
        // Update the main task and its direct dependencies.
        updateIfMissing(_task.id);
        (_task.dependentOn || []).forEach((dependency)=>{
            updateIfMissing(dependency.id);
        });
        // Update any other tasks returned from the date shifts.
        Object.values(payloadTaskUpdates).forEach((item)=>{
            if (item.id !== _task.id) {
                updateIfMissing(item.id);
            }
        });
    } else if (dependentTaskId) {
        // In case of a dependency relation, update both the dependent and the main task.
        const _dependentTask = taskList[dependentTaskId];
        updateIfMissing(_dependentTask.id);
        updateIfMissing(_task.id);
        // Update other tasks from the date shifts, excluding the main and dependent tasks.
        Object.values(payloadTaskUpdates).forEach((item)=>{
            if (item.id !== _dependentTask.id && item.id !== _task.id) {
                updateIfMissing(item.id);
            }
        });
        if (taskUpdates.length > 0) {
            updateTaskDates(taskUpdates);
        }
    }
}
function* createDependencySaga(action) {
    const { payload } = action;
    const queryClient = yield getQueryClientContext();
    if (payload) {
        const { taskId, projectId, dependentTaskId, isSync, toggleUpdating, handleNullDates, dependencyType, newDependenciesSettingEnabled } = payload;
        if (dependentTaskId && dependencyType !== undefined) {
            const data = [
                {
                    entityId: taskId,
                    dependentEntityId: dependentTaskId,
                    dependencyType: DependencyTypesEnumReverse[dependencyType],
                    lag: null,
                    operation: 'ADD'
                }
            ];
            yield put(dependencyCreationThroughTimeline({
                id: taskId,
                type: EntityType.Task,
                payload: {
                    successorId: taskId,
                    dependencyType,
                    lag: null,
                    predecessorId: dependentTaskId
                }
            }));
            let task;
            if (newDependenciesSettingEnabled) {
                task = yield updateDependentTasks(taskId, data);
            } else {
                task = yield createDependentTask(projectId, taskId, {
                    dependentTaskIds: [
                        dependentTaskId
                    ]
                }, isSync);
            }
            yield queryClient.invalidateQueries(getProjectDependencyMapKey(projectId));
            // Refetch task details default fields for both tasks involved in the dependency
            yield queryClient.refetchQueries(getTaskDetailFieldsQueryKey({
                queryString: 'task-details-default-fields',
                projectId,
                taskId
            }));
            yield queryClient.refetchQueries(getTaskDetailFieldsQueryKey({
                queryString: 'task-details-default-fields',
                projectId,
                taskId: dependentTaskId
            }));
            if (isSync && toggleUpdating && task) {
                toggleUpdating(false);
            }
            if (handleNullDates) {
                yield handleEmptyTaskDates(action);
            }
        }
    }
}
/*
Earlier empty task dates were handled using the same action, but after introducing
synchronous date updates ( adding delay in frontend), handleEmptyDates is being called inside creation saga.

The second action we see is being called from drawer, and does not require the deliberate delay there.
 */ export function* watchCreateDependencySaga() {
    yield takeLatest(createDependencySagaAction.type, createDependencySaga);
    yield takeLatest(dependencyCreatedAction.type, handleEmptyTaskDates);
}
